import * as React from 'react';

import {
  Dialog as ShadcnDialog,
  DialogClose as ShadcnDialogClose,
  DialogContent as ShadcnDialogContent,
  DialogDescription as ShadcnDialogDescription,
  DialogFooter as ShadcnDialogFooter,
  DialogHeader as ShadcnDialogHeader,
  DialogTitle as ShadcnDialogTitle,
  DialogTrigger as ShadcnDialogTrigger,
  isContainedPreviewPortal,
  type DialogBackdrop,
} from '@/hen-ui/shadcn/dialog';
import { usePortalContainer } from '@/hen-ui/providers/PortalContainerContext';
import { DrawerTrigger as ShadcnDrawerTrigger } from '@/hen-ui/shadcn/drawer';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { useIsDesktop, useMatchesMedia } from '@/hen-ui/hooks/useIsDesktop';
import {
  modalBodyVariants,
  modalContentVariants,
  modalHeaderVariants,
  type ModalBodyVariantProps,
  type ModalContentVariantProps,
  type ModalHeaderVariantProps,
} from './modal.variants';
import {
  EnclosingModalProvider,
  type ModalOpenPayload,
  useEnclosingModal,
  useModalHost,
} from './ModalHost';
import { ModalSheet } from './ModalSheet';
import {
  collectTriggerChildren,
  TriggerAriaBridge,
} from '@/hen-ui/lib/trigger-children';
import { cn } from '@/hen-ui/utils';

// --- Types ---

export type ModalMobilePresentation = 'center' | 'sheet';
/**
 * Desktop edge for the sheet layout. Unset and legacy `default` open from the
 * right; authoring only offers `left` | `right`.
 */
export type ModalPanelSide = 'default' | 'left' | 'right';

/** Sheet layout, including the legacy `fullscreen-side` catalog value. */
function isSheetLayout(
  variant: ModalContentVariantProps['variant'] | undefined,
): variant is 'sheet' | 'fullscreen-side' {
  return variant === 'sheet' || variant === 'fullscreen-side';
}

/** Map Modal layout onto Dialog chrome (`sheet` is Dialog `side`). */
function toDialogVariant(
  variant: ModalContentVariantProps['variant'] | undefined,
): 'default' | 'fullscreen' | 'side' | undefined {
  if (isSheetLayout(variant)) return 'side';
  if (variant === 'fullscreen') return 'fullscreen';
  if (variant === 'default') return 'default';
  return undefined;
}

function resolveSheetSide(
  panelSide: ModalPanelSide | null | undefined,
): 'left' | 'right' {
  return panelSide === 'left' ? 'left' : 'right';
}
export type ModalBackdrop = DialogBackdrop;

export interface ModalProps {
  /** Whether the modal is open. */
  open?: boolean;
  /** The default open state (uncontrolled). */
  defaultOpen?: boolean;
  /** Callback when the open state changes. */
  onOpenChange?: (open: boolean) => void;
  /** Whether to render the modal in a portal with an overlay. */
  modal?: boolean;
  /**
   * Stable instance id so Buttons/Links elsewhere can open this dialog
   * via the `modal.open` action. Colocated CTAs still live in `trigger`;
   * this is for CTAs in other page regions.
   */
  name?: string;
  /**
   * Catalog slot: colocated buttons or links that open this dialog.
   * Each child is wrapped in `Modal.Trigger`. CTAs in other page regions
   * must use `name` plus `modal.open`, not this slot. Omit this and
   * `content` to keep the compound `children` API (`Modal.Trigger` /
   * `Modal.Content`).
   */
  trigger?: React.ReactNode;
  /**
   * Catalog slot: what the visitor sees inside the dialog.
   * Rendered in `Modal.Content` when set.
   */
  content?: React.ReactNode;
  /**
   * Width token applied to slotted `content`. Ignored on the compound path.
   * Sheet layout defaults to `--width-modal-sheet` (31.25rem) when unset.
   */
  size?: ModalContentVariantProps['size'];
  /** Dialog layout applied to slotted `content`. */
  variant?: ModalContentVariantProps['variant'];
  /**
   * Edge the sheet layout attaches to on desktop. Unset and legacy `default`
   * open from the right. Ignored for the centered and fullscreen layouts.
   */
  panelSide?: ModalPanelSide;
  /** Close (X) control on slotted `content`. Ignored on the compound path. */
  showCloseButton?: boolean;
  /** Title in a pinned header row with the close control; names the dialog. Slotted path only. */
  title?: string;
  /** Corner radius token for slotted `content`. Ignored on `fullscreen`. */
  rounded?: ModalContentVariantProps['rounded'];
  /** Close control style for slotted `content`. */
  closeButtonVariant?: ModalContentProps['closeButtonVariant'];
  /** Surface painted across the whole dialog for slotted `content`. */
  surface?: ModalContentProps['surface'];
  /**
   * Inner spacing around slotted `content`, below the header row (Body padding).
   * Sheet layout defaults to 1.25rem (`p-modal-sheet-padding`) when unset.
   */
  padding?: ModalBodyVariantProps['padding'];
  /** Page dim: `default` is 40% black, `tinted` is `--neutral-700` at 50%. Also applies to the mobile sheet. */
  backdrop?: ModalBackdrop;
  /** Centered dialog elevation; unset keeps `md`. */
  shadow?: ModalContentVariantProps['shadow'];
  /** Centered dialog only: scrolls the body past `--max-height-modal-body` (500px); the title row and close stay put. */
  capBodyHeight?: boolean;
  /**
   * Side spacing on the sticky title row (Header padding). Top and bottom
   * stay fixed so the title does not move vertically. Unset keeps `md`.
   */
  headerPadding?: ModalHeaderVariantProps['padding'];
  /**
   * How slotted `content` presents below `md` for the centered layout.
   * `sheet` rises from the bottom and can be swiped down to close; `md` and
   * up stays the centered dialog. Unset keeps the centered dialog everywhere.
   * Ignored when `variant` is `sheet` (a tall bottom sheet up to 768px, full
   * height at 768px with its top corners kept, and a side panel from 769px) or
   * `fullscreen` (edge to edge at every width). Ignored on the compound path.
   * Crossing `md` while open keeps it open but remounts `content`, so state
   * inside it (e.g. typed form input) resets.
   */
  mobilePresentation?: ModalMobilePresentation;
  /** Pull bar atop the phone sheet from `mobilePresentation: 'sheet'`; off by default.
   * Ignored for `variant: 'sheet'` and at `md` and up; swipe-down closes either way. */
  showSheetHandle?: boolean;
  /** Modal content (Trigger, Content, etc.). */
  children?: React.ReactNode;
}

export interface ModalTriggerProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDialogTrigger> {}

export interface ModalContentProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof ShadcnDialogContent>,
      'side' | 'variant'
    >,
    ModalContentVariantProps {
  /**
   * Overrides the passive `hen.modal` identity so a composite built on Modal can be told apart
   * from a generic modal — same consumer-wins precedence as `Card`.
   */
  'data-hen-id'?: string;
  /** Edge the sheet layout attaches to on desktop. `default` opens from the right. */
  panelSide?: ModalPanelSide;
}

export interface ModalHeaderProps
  extends React.ComponentPropsWithoutRef<'div'> {}

export interface ModalFooterProps
  extends React.ComponentPropsWithoutRef<'div'> {}

export interface ModalTitleProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDialogTitle> {}

export interface ModalDescriptionProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDialogDescription> {}

export interface ModalCloseProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDialogClose> {}

// --- Sub-components ---

const TRIGGER_HOST_CLASS = 'inline-flex w-fit self-start';

type TriggerWrapper = React.ComponentType<{
  asChild?: boolean;
  className?: string;
  children?: React.ReactNode;
}>;

function wrapModalTriggers(
  trigger: React.ReactNode,
  Trigger: TriggerWrapper = ModalTrigger,
): React.ReactNode {
  return collectTriggerChildren(trigger).map((child, index) => {
    if (child == null) {
      return null;
    }

    // Merge trigger props onto the CTA itself so aria-expanded / aria-controls
    // land on a real button (or other control), not a wrapping span.
    if (React.isValidElement(child)) {
      const element = child as React.ReactElement<{ className?: string }>;
      return (
        <Trigger key={`modal-trigger-${index}`} asChild>
          <TriggerAriaBridge asChild>
            {React.cloneElement(element, {
              className: cn(TRIGGER_HOST_CLASS, element.props.className),
            })}
          </TriggerAriaBridge>
        </Trigger>
      );
    }

    return (
      <Trigger key={`modal-trigger-${index}`} asChild>
        <TriggerAriaBridge className={TRIGGER_HOST_CLASS} role='button'>
          {child}
        </TriggerAriaBridge>
      </Trigger>
    );
  });
}

function useNamedModalOpen(
  name: string | undefined,
  open: boolean | undefined,
  onOpenChange: ((open: boolean) => void) | undefined,
  defaultOpen: boolean | undefined,
): Pick<ModalProps, 'open' | 'onOpenChange'> & {
  payload: ModalOpenPayload | undefined;
} {
  const host = useModalHost();
  const instanceName = name?.trim() ?? '';
  const isNamed = instanceName.length > 0;
  // Catalog props are serializable, so authoring can persist `open` without
  // supplying the callback required to control it. Treat that shape as an
  // initial/default hint for unnamed dialogs only. A named Modal without an
  // onOpenChange callback must remain owned by ModalHost so modal.open and
  // modal.close can change its state.
  const isControlled = open !== undefined && onOpenChange !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  );
  const [payload, setPayload] = React.useState<ModalOpenPayload>();

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  // A trigger or a plain `modal.open` must not inherit the previous payload.
  // Closing keeps it so content does not change during the exit animation.
  const openFromHost = React.useCallback(
    (next: boolean, nextPayload?: ModalOpenPayload) => {
      if (next) setPayload(nextPayload);
      setOpen(next);
      if (!next && isNamed) {
        host.notifyClosed(instanceName);
      }
    },
    [host, instanceName, isNamed, setOpen],
  );

  const openFromDialog = React.useCallback(
    (next: boolean) => openFromHost(next),
    [openFromHost],
  );

  React.useLayoutEffect(() => {
    if (!isNamed) {
      return undefined;
    }
    return host.register(instanceName, openFromHost);
  }, [host, instanceName, isNamed, openFromHost]);

  if (!isNamed) {
    return { open, onOpenChange, payload: undefined };
  }

  return {
    open: isControlled ? open : uncontrolledOpen,
    onOpenChange: openFromDialog,
    payload,
  };
}

interface ResponsiveModalProps
  extends Pick<
    ModalProps,
    | 'open'
    | 'defaultOpen'
    | 'onOpenChange'
    | 'modal'
    | 'trigger'
    | 'content'
    | 'size'
    | 'variant'
    | 'panelSide'
    | 'showCloseButton'
    | 'rounded'
    | 'closeButtonVariant'
    | 'surface'
    | 'backdrop'
    | 'shadow'
    | 'capBodyHeight'
    | 'showSheetHandle'
  > {
  /** Trimmed `title`; unset when blank. */
  headerTitle?: string;
  headerClassName?: string;
  bodyClassName?: string;
}

/** Sheet layout docks to a side from here; 768px and narrower keep the bottom sheet. */
const SIDE_PANEL_QUERY = '(min-width: 769px)';

function viewportCanBeMeasured(): boolean {
  return (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  );
}

function modalBodyClassName(
  padding: ModalProps['padding'],
  options?: { sheetLayout?: boolean },
): string | undefined {
  // Sheet layout defaults to 1.25rem; Body padding still overrides (including `none`).
  if (options?.sheetLayout) {
    if (padding === 'none') return undefined;
    if (padding) return modalBodyVariants({ padding });
    return 'p-modal-sheet-padding';
  }
  return padding && padding !== 'none'
    ? modalBodyVariants({ padding })
    : undefined;
}

function modalHeaderClassName(
  headerPadding: ModalProps['headerPadding'],
): string {
  if (headerPadding) return modalHeaderVariants({ padding: headerPadding });
  return modalHeaderVariants({ padding: 'md' });
}

/**
 * Slotted Modal with `mobilePresentation: 'sheet'`. Owns the open state so a
 * viewport crossing `md` swaps presentation without closing. The authoring
 * preview keeps the centered dialog, which already handles its iframe.
 *
 * The sheet and dialog are separate trees, so trigger CTAs and `content`
 * remount when the presentation swaps. Radix triggers must live inside the
 * root that is showing; Vaul creates its own root.
 */
const ResponsiveModal: React.FC<ResponsiveModalProps> = ({
  open,
  defaultOpen,
  onOpenChange,
  modal,
  trigger,
  content,
  size,
  variant,
  panelSide,
  showCloseButton,
  rounded,
  closeButtonVariant,
  surface,
  backdrop,
  shadow,
  capBodyHeight,
  showSheetHandle,
  headerTitle,
  headerClassName,
  bodyClassName,
}) => {
  const isDesktop = useIsDesktop();
  const isSidePanelWidth = useMatchesMedia(SIDE_PANEL_QUERY);
  const contained = isContainedPreviewPortal(usePortalContainer());
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  );
  // `useIsDesktop` settles after the first paint. Hold the open state until
  // then so "Open on load" does not flash the sheet on desktop.
  const [measured, setMeasured] = React.useState(false);
  React.useEffect(() => setMeasured(true), []);
  const resolvedOpen = measured && (open ?? uncontrolledOpen);

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (open === undefined) {
        setUncontrolledOpen(next);
      }
      onOpenChange?.(next);
    },
    [onOpenChange, open],
  );

  // Wait for `measured` so SSR and the first client paint share one tree;
  // without a measurable viewport the sheet layout keeps its desktop panel.
  const isSheet = isSheetLayout(variant);
  const sheetLayoutAdapts = isSheet && measured && viewportCanBeMeasured();

  const showsBottomSheet = isSheet
    ? sheetLayoutAdapts && !isSidePanelWidth
    : !isDesktop;

  if (showsBottomSheet && !contained) {
    return (
      <ModalSheet
        open={resolvedOpen}
        onOpenChange={setOpen}
        modal={modal}
        showCloseButton={showCloseButton}
        trigger={wrapModalTriggers(trigger, ShadcnDrawerTrigger)}
        title={headerTitle}
        rounded={rounded}
        closeButtonVariant={closeButtonVariant}
        surface={surface}
        backdrop={backdrop}
        headerClassName={headerClassName}
        bodyClassName={bodyClassName}
        tall={isSheet}
        showHandle={showSheetHandle}
      >
        {content}
      </ModalSheet>
    );
  }

  return (
    <ShadcnDialog open={resolvedOpen} onOpenChange={setOpen} modal={modal}>
      {wrapModalTriggers(trigger)}
      <ModalContent
        size={size}
        variant={variant}
        panelSide={panelSide}
        rounded={rounded}
        surface={surface}
        backdrop={backdrop}
        shadow={shadow}
        capBodyHeight={capBodyHeight}
        closeButtonVariant={closeButtonVariant}
        showCloseButton={showCloseButton}
        headerTitle={headerTitle}
        headerClassName={headerClassName}
        bodyClassName={bodyClassName}
        aria-describedby={undefined}
      >
        {content}
      </ModalContent>
    </ShadcnDialog>
  );
};

const ModalRoot: React.FC<ModalProps> = ({
  trigger,
  content,
  size,
  variant,
  panelSide,
  showCloseButton,
  title,
  rounded,
  closeButtonVariant,
  surface,
  padding,
  backdrop,
  shadow,
  capBodyHeight,
  headerPadding,
  mobilePresentation,
  showSheetHandle,
  children,
  name,
  open,
  onOpenChange,
  defaultOpen,
  ...props
}) => {
  const {
    payload,
    onOpenChange: dialogOnOpenChange,
    ...namedOpen
  } = useNamedModalOpen(name, open, onOpenChange, defaultOpen);
  // Without an `open` value, Radix holds the open state and a callback alone
  // cannot close it. Own the state and still forward every change.
  const dialogIsUncontrolled = namedOpen.open === undefined;
  const [fallbackOpen, setFallbackOpen] = React.useState(defaultOpen ?? false);
  const handleUncontrolledOpenChange = React.useCallback(
    (next: boolean) => {
      setFallbackOpen(next);
      dialogOnOpenChange?.(next);
    },
    [dialogOnOpenChange],
  );
  const requestClose = React.useCallback(() => {
    if (dialogIsUncontrolled) {
      handleUncontrolledOpenChange(false);
      return;
    }
    dialogOnOpenChange?.(false);
  }, [dialogIsUncontrolled, dialogOnOpenChange, handleUncontrolledOpenChange]);
  const dialogProps = {
    ...props,
    ...namedOpen,
    onOpenChange: dialogIsUncontrolled
      ? handleUncontrolledOpenChange
      : dialogOnOpenChange,
    ...(dialogIsUncontrolled ? { open: fallbackOpen } : {}),
  };

  const isSlotted = trigger !== undefined || content !== undefined;
  // The compound API places its own Modal.Content, whose close defaults on
  // until that content reports it turned the close off.
  const [contentOmitsClose, setContentOmitsClose] = React.useState(false);
  const showsCloseButton =
    (!isSlotted || showCloseButton !== false) && !contentOmitsClose;
  const [closeButtonHidden, setCloseButtonHidden] = React.useState(false);
  const [titleHidden, setTitleHidden] = React.useState(false);
  const enclosingModal = React.useMemo(
    () => ({
      payload,
      showsCloseButton,
      setCloseButtonHidden,
      setTitleHidden,
      setContentOmitsClose,
      requestClose,
    }),
    [payload, requestClose, showsCloseButton],
  );
  const slottedShowCloseButton = closeButtonHidden ? false : showCloseButton;
  const headerTitle = title?.trim() || undefined;
  const headerClassName = modalHeaderClassName(headerPadding);
  const bodyClassName = modalBodyClassName(padding, {
    sheetLayout: isSheetLayout(variant),
  });

  if (
    isSlotted &&
    (isSheetLayout(variant) ||
      (mobilePresentation === 'sheet' && variant !== 'fullscreen'))
  ) {
    return (
      <ResponsiveModal
        {...dialogProps}
        trigger={trigger}
        content={
          <EnclosingModalProvider value={enclosingModal}>
            {content}
          </EnclosingModalProvider>
        }
        size={size}
        variant={variant}
        panelSide={panelSide}
        showCloseButton={slottedShowCloseButton}
        rounded={rounded}
        closeButtonVariant={closeButtonVariant}
        surface={surface}
        backdrop={backdrop}
        shadow={shadow}
        capBodyHeight={capBodyHeight}
        showSheetHandle={showSheetHandle}
        headerTitle={headerTitle}
        headerClassName={headerClassName}
        bodyClassName={bodyClassName}
      />
    );
  }

  if (isSlotted) {
    const hidesTitle = titleHidden && headerTitle !== undefined;
    return (
      <ShadcnDialog {...dialogProps}>
        {wrapModalTriggers(trigger)}
        <ModalContent
          size={size}
          variant={variant}
          panelSide={panelSide}
          rounded={rounded}
          surface={surface}
          backdrop={backdrop}
          shadow={shadow}
          capBodyHeight={capBodyHeight}
          closeButtonVariant={closeButtonVariant}
          showCloseButton={slottedShowCloseButton}
          headerTitle={hidesTitle ? undefined : headerTitle}
          headerClassName={headerClassName}
          bodyClassName={bodyClassName}
          aria-describedby={undefined}
          // Fixed content, such as the gallery carousel, covers this popup
          // and not the whole window when the editor preview contains it.
          className={variant === 'fullscreen' ? '[contain:layout]' : undefined}
        >
          {hidesTitle ? (
            <ModalTitle className='sr-only'>{headerTitle}</ModalTitle>
          ) : null}
          <EnclosingModalProvider value={enclosingModal}>
            {content}
          </EnclosingModalProvider>
        </ModalContent>
      </ShadcnDialog>
    );
  }

  return (
    <ShadcnDialog {...dialogProps}>
      <EnclosingModalProvider value={enclosingModal}>
        {children}
      </EnclosingModalProvider>
    </ShadcnDialog>
  );
};

ModalRoot.displayName = 'Modal';

/** When `asChild` is set, identity merges onto the child (e.g. `Button` carries `hen.button`); passive `hen.modal` stays on `ModalContent`. */
export const ModalTrigger = React.forwardRef<
  React.ElementRef<typeof ShadcnDialogTrigger>,
  ModalTriggerProps
>(({ className, ...props }, ref) => {
  const { asChild, ...rest } = props;
  return (
    <ShadcnDialogTrigger
      className={cn(className)}
      ref={ref}
      asChild={asChild}
      {...rest}
      {...(!asChild ? { 'data-hen-id': COMPONENT_IDS.Modal } : {})}
    />
  );
});

ModalTrigger.displayName = 'ModalTrigger';

export const ModalContent = React.forwardRef<
  React.ElementRef<typeof ShadcnDialogContent>,
  ModalContentProps
>(
  (
    {
      className,
      size,
      variant,
      panelSide,
      rounded,
      shadow,
      'data-hen-id': dataHenId = COMPONENT_IDS.Modal,
      showCloseButton,
      ...props
    },
    ref,
  ) => {
    // Only the compound API renders Modal.Content inside the provider. A
    // slotted Modal passes its close on the outside, so this sees no provider
    // and leaves that close alone.
    const setContentOmitsClose = useEnclosingModal()?.setContentOmitsClose;
    React.useLayoutEffect(() => {
      if (!setContentOmitsClose || showCloseButton !== false) return;
      setContentOmitsClose(true);
      return () => setContentOmitsClose(false);
    }, [setContentOmitsClose, showCloseButton]);

    const isSheet = isSheetLayout(variant);
    // Sheet defaults to `--width-modal-sheet` (31.25rem) via the variant class;
    // authors still pick Width to override.
    const resolvedSize =
      variant === 'fullscreen' ? null : isSheet ? (size ?? null) : size;
    return (
      <ShadcnDialogContent
        showCloseButton={showCloseButton}
        variant={toDialogVariant(variant)}
        side={isSheet ? resolveSheetSide(panelSide) : undefined}
        className={cn(
          modalContentVariants({
            size: resolvedSize,
            rounded: variant === 'fullscreen' || isSheet ? null : rounded,
            shadow: variant === 'fullscreen' || isSheet ? null : shadow,
            variant: isSheet ? 'sheet' : variant,
          }),
          className,
        )}
        ref={ref}
        {...props}
        data-hen-id={dataHenId}
      />
    );
  },
);

ModalContent.displayName = 'ModalContent';

export const ModalHeader = React.forwardRef<HTMLDivElement, ModalHeaderProps>(
  ({ className, ...props }, ref) => {
    return (
      <ShadcnDialogHeader className={cn(className)} ref={ref} {...props} />
    );
  },
);

ModalHeader.displayName = 'ModalHeader';

export const ModalFooter = React.forwardRef<HTMLDivElement, ModalFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <ShadcnDialogFooter className={cn(className)} ref={ref} {...props} />
    );
  },
);

ModalFooter.displayName = 'ModalFooter';

export const ModalTitle = React.forwardRef<
  React.ElementRef<typeof ShadcnDialogTitle>,
  ModalTitleProps
>(({ className, ...props }, ref) => {
  return <ShadcnDialogTitle className={cn(className)} ref={ref} {...props} />;
});

ModalTitle.displayName = 'ModalTitle';

export const ModalDescription = React.forwardRef<
  React.ElementRef<typeof ShadcnDialogDescription>,
  ModalDescriptionProps
>(({ className, ...props }, ref) => {
  return (
    <ShadcnDialogDescription className={cn(className)} ref={ref} {...props} />
  );
});

ModalDescription.displayName = 'ModalDescription';

export const ModalClose = React.forwardRef<
  React.ElementRef<typeof ShadcnDialogClose>,
  ModalCloseProps
>(({ className, ...props }, ref) => {
  return <ShadcnDialogClose className={cn(className)} ref={ref} {...props} />;
});

ModalClose.displayName = 'ModalClose';

// --- Compound Component ---

export const Modal = Object.assign(ModalRoot, {
  Trigger: ModalTrigger,
  Content: ModalContent,
  Header: ModalHeader,
  Title: ModalTitle,
  Description: ModalDescription,
  Footer: ModalFooter,
  Close: ModalClose,
});
